import { useEffect, useState } from 'react';
import { useApiClient } from '../lib/apiClient';
import { errorCopy } from '../lib/errors';
import { combineLocal } from '../lib/datetime';
import { formatARS } from '../lib/format';

interface Service {
  id: string;
  name: string;
  price_cents: number;
  duration_min: number;
  active: number;
}

interface Barber {
  id: string;
  name: string;
  role: 'owner' | 'barber';
  active: number;
}

interface Props {
  date: string;          // YYYY-MM-DD
  time?: string;         // HH:MM preseteado (opcional)
  barbers: Barber[];
  services: Service[];
  defaultBarberId?: string;
  onClose: () => void;
  onCreated: () => void;
}

export default function NewAppointmentModal({ date, time, barbers, services, defaultBarberId, onClose, onCreated }: Props) {
  const apiClient = useApiClient();
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [barberId, setBarberId] = useState(defaultBarberId || barbers[0]?.id || '');
  const [serviceId, setServiceId] = useState(services[0]?.id || '');
  const [localTime, setLocalTime] = useState(time ?? '10:00');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (defaultBarberId) setBarberId(defaultBarberId);
  }, [defaultBarberId]);

  async function submit() {
    setError(null);
    if (!clientName.trim() || !clientPhone.trim()) { setError('Faltan nombre o WhatsApp del cliente.'); return; }
    if (!barberId || !serviceId) { setError('Elegí barbero y servicio.'); return; }

    setSubmitting(true);
    const starts_at = combineLocal(date, localTime);
    const res = await apiClient<{ appointment: { id: string } }>('/api/appointments', {
      method: 'POST',
      body: {
        client: { name: clientName, phone: clientPhone },
        barber_id: barberId,
        service_id: serviceId,
        starts_at,
      },
    });
    setSubmitting(false);
    if (!res.ok) {
      if (res.error === 'slot_taken') setError('Ese horario ya tiene un turno con ese barbero.');
      else setError(errorCopy(res.error));
      return;
    }
    onCreated();
    onClose();
  }

  const selectedService = services.find((s) => s.id === serviceId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-bone rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-2xl font-semibold tracking-tighter mb-1">Nuevo turno</h2>
        <p className="text-sm text-ash mb-6">{date} · <span className="num">{localTime}</span></p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre del cliente" value={clientName} onChange={setClientName} placeholder="Juan Pérez" />
            <Field label="WhatsApp" value={clientPhone} onChange={setClientPhone} placeholder="11 1234 5678" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select label="Barbero" value={barberId} onChange={setBarberId}>
              {barbers.filter((b) => b.active).map((b) => (
                <option key={b.id} value={b.id}>{b.name}{b.role === 'owner' ? ' (vos)' : ''}</option>
              ))}
            </Select>
            <Select label="Servicio" value={serviceId} onChange={setServiceId}>
              {services.filter((s) => s.active).map((s) => (
                <option key={s.id} value={s.id}>{s.name} · {formatARS(s.price_cents)} · {s.duration_min}min</option>
              ))}
            </Select>
          </div>

          <Field label="Hora" value={localTime} onChange={setLocalTime} type="time" />

          {selectedService && (
            <div className="text-sm text-ink/70 bg-white border border-ink/10 rounded-lg px-4 py-3">
              <span className="font-medium">{formatARS(selectedService.price_cents)}</span>
              {' · termina '}
              <span className="num">{addMin(localTime, selectedService.duration_min)}</span>
            </div>
          )}

          {error && <div className="text-sm text-danger bg-danger/5 border border-danger/20 rounded-lg px-4 py-3">{error}</div>}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-ink/60 hover:text-ink">Cancelar</button>
          <button onClick={submit} disabled={submitting} className="btn-primary disabled:opacity-50">
            {submitting ? 'Guardando…' : 'Reservar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-ash mb-1.5 block">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-lg border border-ink/15 bg-white px-3 py-2.5 focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none" />
    </label>
  );
}

function Select({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-ash mb-1.5 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-ink/15 bg-white px-3 py-2.5 focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none">
        {children}
      </select>
    </label>
  );
}

function addMin(time: string, min: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = (h ?? 0) * 60 + (m ?? 0) + min;
  const nh = Math.floor(total / 60) % 24;
  const nm = total % 60;
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
}
